<script>
import Layout from "../../layouts/main";
import PageHeader from "@/components/page-header";
import { apiGet } from "@/utils/request";
import { API_ENDPOINTS } from "@/constants";
import { requireAuth } from "@/utils/auth";

/**
 * Dashboard component - 控制台页面
 */
export default {
  components: { Layout, PageHeader },
  data() {
    return {
      title: "Dashboard",
      items: [
        {
          text: "Dashboard",
          active: true,
        },
      ],
      loading: true,
      statistics: {
        domain_count: 0,
        ptr_count: 0,
        total_records: 0,
        enabled_domains: 0,
        recent_logs_count: 0,
        recent_logs: []
      },
      // 快捷入口配置
      quickActions: [
        {
          id: 1,
          title: "DNS 列表",
          icon: "ri-global-line",
          color: "primary",
          route: "/dns/list",
          description: "管理域名解析"
        },
        {
          id: 2,
          title: "PTR 管理",
          icon: "ri-arrow-left-right-line",
          color: "info",
          route: "/dns/ptr",
          description: "管理反向解析"
        },
        {
          id: 3,
          title: "添加域名",
          icon: "ri-add-circle-line",
          color: "success",
          action: "addDomain",
          description: "添加新域名"
        },
        {
          id: 4,
          title: "操作日志",
          icon: "ri-file-list-3-line",
          color: "warning",
          route: "/setting/logs",
          description: "查看操作记录"
        },
        {
          id: 5,
          title: "账户设置",
          icon: "ri-user-settings-line",
          color: "secondary",
          route: "/setting/account",
          description: "账户信息设置"
        },
        {
          id: 6,
          title: "安全设置",
          icon: "ri-shield-check-line",
          color: "danger",
          route: "/setting/safety",
          description: "安全与隐私"
        }
      ]
    };
  },
  mounted() {
    this.title = this.$t("dashboard.text") || "控制台";
    this.items = [
      {
        text: this.$t("dashboard.text") || "控制台",
        href: "/",
      },
    ];
    this.loadStatistics();
  },
  methods: {
    /**
     * 加载统计信息
     */
    async loadStatistics() {
      if (!requireAuth(this)) {
        return;
      }
      
      try {
        const response = await apiGet(API_ENDPOINTS.DASHBOARD_STATISTICS, {}, {
          vm: this,
          showLoading: true,
          loadingText: '加载统计信息...',
          showError: false
        });
        
        if (response && response.status === 200 && response.data) {
          this.statistics = response.data;
        }
      } catch (error) {
        console.error('加载统计信息失败:', error);
        // 如果接口不存在，静默失败
      } finally {
        this.loading = false;
      }
    },
    
    /**
     * 跳转到指定路由
     */
    navigateTo(route) {
      if (route) {
        this.$router.push(route);
      }
    },
    
    /**
     * 执行快捷操作
     */
    handleQuickAction(action) {
      if (action === 'addDomain') {
        this.addDomain();
      } else if (action.route) {
        this.navigateTo(action.route);
      }
    },
    
    /**
     * 添加域名
     */
    async addDomain() {
      try {
        const { value } = await this.$prompt(
          '请输入要添加的域名',
          '添加域名',
          {
            confirmButtonText: '确定',
            cancelButtonText: '取消',
            inputPattern: /^[a-zA-Z0-9][a-zA-Z0-9-]{0,61}[a-zA-Z0-9]?\.[a-zA-Z]{2,}$/,
            inputErrorMessage: '请输入有效的域名'
          }
        );
        
        if (value) {
          // 跳转到 DNS 列表页面，由列表页面处理添加
          this.$router.push('/dns/list');
          // 延迟触发添加操作（等待页面加载）
          this.$nextTick(() => {
            this.$root.$emit('add-domain', value);
          });
        }
      } catch (error) {
        // 用户取消操作
      }
    }
  },
};
</script>

<template>
  <Layout>
    <PageHeader :title="title" :items="items" />
    
    <div class="row" v-loading="loading">
      <!-- 统计卡片 -->
      <div class="col-xl-3 col-md-6">
        <div class="card">
          <div class="card-body">
            <div class="d-flex align-items-center">
              <div class="flex-shrink-0">
                <div class="avatar-sm rounded-circle bg-soft-primary text-primary d-flex align-items-center justify-content-center">
                  <i class="ri-global-line font-size-24"></i>
                </div>
              </div>
              <div class="flex-grow-1 ms-3">
                <p class="text-uppercase fw-medium text-muted mb-0">域名数量</p>
                <h4 class="mb-0">{{ statistics.domain_count }}</h4>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      <div class="col-xl-3 col-md-6">
        <div class="card">
          <div class="card-body">
            <div class="d-flex align-items-center">
              <div class="flex-shrink-0">
                <div class="avatar-sm rounded-circle bg-soft-info text-info d-flex align-items-center justify-content-center">
                  <i class="ri-arrow-left-right-line font-size-24"></i>
                </div>
              </div>
              <div class="flex-grow-1 ms-3">
                <p class="text-uppercase fw-medium text-muted mb-0">PTR 数量</p>
                <h4 class="mb-0">{{ statistics.ptr_count }}</h4>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      <div class="col-xl-3 col-md-6">
        <div class="card">
          <div class="card-body">
            <div class="d-flex align-items-center">
              <div class="flex-shrink-0">
                <div class="avatar-sm rounded-circle bg-soft-success text-success d-flex align-items-center justify-content-center">
                  <i class="ri-file-list-line font-size-24"></i>
                </div>
              </div>
              <div class="flex-grow-1 ms-3">
                <p class="text-uppercase fw-medium text-muted mb-0">总记录数</p>
                <h4 class="mb-0">{{ statistics.total_records }}</h4>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      <div class="col-xl-3 col-md-6">
        <div class="card">
          <div class="card-body">
            <div class="d-flex align-items-center">
              <div class="flex-shrink-0">
                <div class="avatar-sm rounded-circle bg-soft-warning text-warning d-flex align-items-center justify-content-center">
                  <i class="ri-checkbox-circle-line font-size-24"></i>
                </div>
              </div>
              <div class="flex-grow-1 ms-3">
                <p class="text-uppercase fw-medium text-muted mb-0">启用域名</p>
                <h4 class="mb-0">{{ statistics.enabled_domains }}</h4>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    
    <!-- 快捷入口 -->
    <div class="row mt-4">
      <div class="col-12">
        <div class="card">
          <div class="card-header">
            <h5 class="card-title mb-0">
              <i class="ri-apps-2-line me-2"></i>功能快捷入口
            </h5>
          </div>
          <div class="card-body">
            <div class="row g-3">
              <div 
                v-for="action in quickActions" 
                :key="action.id"
                class="col-xl-4 col-md-6 col-sm-6"
              >
                <div 
                  class="card border shadow-sm quick-action-card"
                  @click="handleQuickAction(action)"
                  style="cursor: pointer; transition: all 0.3s ease;"
                  @mouseenter="$event.currentTarget.style.transform = 'translateY(-5px)'"
                  @mouseleave="$event.currentTarget.style.transform = 'translateY(0)'"
                >
                  <div class="card-body text-center p-4">
                    <div class="mb-3">
                      <div 
                        :class="`avatar-lg rounded-circle bg-soft-${action.color} text-${action.color} d-inline-flex align-items-center justify-content-center`"
                        style="width: 64px; height: 64px;"
                      >
                        <i :class="`${action.icon} font-size-28`"></i>
                      </div>
                    </div>
                    <h5 class="mb-2">{{ action.title }}</h5>
                    <p class="text-muted mb-0 small">{{ action.description }}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    
    <!-- 最近操作日志 -->
    <div class="row mt-4" v-if="statistics.recent_logs && statistics.recent_logs.length > 0">
      <div class="col-12">
        <div class="card">
          <div class="card-header d-flex justify-content-between align-items-center">
            <h5 class="card-title mb-0">
              <i class="ri-file-list-3-line me-2"></i>最近操作
            </h5>
            <el-link 
              type="primary" 
              :underline="false"
              @click="navigateTo('/setting/logs')"
            >
              查看全部 <i class="ri-arrow-right-line"></i>
            </el-link>
          </div>
          <div class="card-body">
            <div class="table-responsive">
              <table class="table table-hover mb-0">
                <thead>
                  <tr>
                    <th>操作</th>
                    <th>域名</th>
                    <th>记录</th>
                    <th>时间</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="log in statistics.recent_logs" :key="log.id">
                    <td>
                      <span class="badge bg-soft-primary text-primary">{{ log.action }}</span>
                    </td>
                    <td>{{ log.domain || log.target || '-' }}</td>
                    <td>{{ log.record || '-' }}</td>
                    <td>{{ log.time || '-' }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  </Layout>
</template>

<style scoped>
.quick-action-card {
  border: 1px solid #e9ecef;
}

.quick-action-card:hover {
  box-shadow: 0 0.5rem 1rem rgba(0, 0, 0, 0.15) !important;
  border-color: #0d6efd;
}

.avatar-sm {
  width: 48px;
  height: 48px;
}

.avatar-lg {
  width: 64px;
  height: 64px;
}

.bg-soft-primary {
  background-color: rgba(13, 110, 253, 0.1);
}

.bg-soft-info {
  background-color: rgba(13, 202, 240, 0.1);
}

.bg-soft-success {
  background-color: rgba(25, 135, 84, 0.1);
}

.bg-soft-warning {
  background-color: rgba(255, 193, 7, 0.1);
}

.bg-soft-danger {
  background-color: rgba(220, 53, 69, 0.1);
}

.bg-soft-secondary {
  background-color: rgba(108, 117, 125, 0.1);
}

.text-primary {
  color: #0d6efd !important;
}

.text-info {
  color: #0dcaf0 !important;
}

.text-success {
  color: #198754 !important;
}

.text-warning {
  color: #ffc107 !important;
}

.text-danger {
  color: #dc3545 !important;
}

.text-secondary {
  color: #6c757d !important;
}

.table th {
  border-top: none;
  font-weight: 600;
  color: #495057;
}

.table td {
  vertical-align: middle;
}

.badge {
  padding: 0.35em 0.65em;
  font-weight: 500;
}
</style>
